/* ─────────────────────────────────────────────────────────────
   BotonDescargarCredenciales.jsx — admin only
   Descarga y regenera el Excel de credenciales del sistema.
   ───────────────────────────────────────────────────────────── */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function dateTag() {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
}

export default function BotonDescargarCredenciales() {
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  if (user?.rol !== "admin") return null;

  const descargar = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/admin/credenciales/download`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `credenciales_DMU_${dateTag()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch (e) {
      toast.error(
        e.response?.status === 403
          ? "Solo el rol admin puede descargar este archivo."
          : e.response?.data?.detail || e.message
      );
    } finally {
      setDownloading(false);
    }
  };

  const regenerar = async () => {
    setShowConfirm(false);
    setRegenerating(true);
    try {
      const r = await axios.post(`${API}/admin/credenciales/regenerar`);
      toast.success(
        `Archivo regenerado (${(r.data.size_bytes / 1024).toFixed(1)} KB). Descargando…`
      );
      // refresh automático de la descarga
      await descargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <>
      <div className="modo-demo-admin" data-testid="admin-credenciales">
        <h3>🔐 Credenciales del sistema</h3>
        <p className="text-sm text-slate-500 mb-3">
          Respaldo offline de todos los usuarios y passwords. Documento confidencial.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            className="btn-primary"
            onClick={descargar}
            disabled={downloading || regenerating}
            data-testid="btn-descargar-credenciales"
          >
            <i className="fa-solid fa-file-arrow-down mr-1.5" />
            {downloading ? "Descargando…" : "Descargar credenciales (.xlsx)"}
          </button>
          <button
            className="btn-ghost"
            onClick={() => setShowConfirm(true)}
            disabled={downloading || regenerating}
            data-testid="btn-regenerar-credenciales"
            style={{ padding: "0.55rem 1rem" }}
          >
            <i className="fa-solid fa-arrows-rotate mr-1.5" />
            {regenerating ? "Regenerando…" : "Regenerar archivo"}
          </button>
        </div>
      </div>

      {showConfirm && (
        <div
          className="md-modal-bg"
          onClick={() => setShowConfirm(false)}
          data-testid="modal-confirm-regenerar"
        >
          <div className="md-modal" onClick={(e) => e.stopPropagation()}>
            <h2>
              <i className="fa-solid fa-arrows-rotate text-blue-600 mr-2" />
              ¿Regenerar archivo de credenciales?
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Esto actualizará el archivo con cualquier <strong>usuario nuevo</strong> agregado a la
              base de datos desde el último export.
            </p>
            <p className="text-sm text-slate-600 leading-relaxed mt-2">
              <i className="fa-solid fa-circle-check text-emerald-600 mr-1" />
              <strong>Las contraseñas existentes NO cambiarán.</strong> Solo se generan nuevas
              passwords temporales para usuarios que aún no tienen una.
            </p>
            <div className="md-foot">
              <button
                className="btn-ghost-sm"
                onClick={() => setShowConfirm(false)}
                data-testid="btn-cancel-regenerar"
              >
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={regenerar}
                data-testid="btn-confirm-regenerar"
              >
                Sí, regenerar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
